import { Controller } from '@hotwired/stimulus'
import imagesLoaded from 'imagesloaded'
import { Scraper, sendError, scrollTo } from '../utils'
import * as phone from '../utils/phone'

export default class extends Controller {
  static targets = [
    'url',
    'loader',
    'urlError',
    'error',
    'name',
    'amount',
    'currency',
    'cover',
    'pictureTemplate',
    'pictures',
    'picture',
    'uploadSlot',
    'clearButton',
    'refresh',
    'coverFile',
    'errorMessage',
    'removeCover',
    'scrapeResult',
  ]

  static values = {
    baseUrl: String,
    apiScrapedUrlPattern: String,
    invalidError: String,
    defaultError: String,
    autoScrap: { type: Boolean, default: false },
    imageMinWidth: { type: Number, default: 250 },
    imageMinHeight: { type: Number, default: 300 },
    imageMaxRatio: { type: Number, default: 3 },
    currencies: Array,
  }

  static classes = ['hidden']

  connect() {
    this.processed = !!this.urlTarget.value
    this.processing = false
    this.productFound = false
    this.coverTarget.disabled = true
    // Le snapshot Turbo part vierge (form_reset.js appelle form.reset() à
    // turbo:before-cache) : reset() ne touche qu'aux champs, les vignettes
    // ajoutées par JS (photo uploadée, images scrapées) restaient dans le
    // snapshot et réapparaissaient une image à la réouverture du formulaire.
    this.element.addEventListener('reset', this.#onFormReset)
    if (this.urlTarget.value) this.clearButtonTarget.classList.remove(this.hiddenClass)

    // Édition : la cover actuelle est une vignette (attribut `existing`) — anneau
    // de sélection posé ici, le serveur ne peut pas piloter la propriété du
    // custom element.
    const existing = this.pictureTargets.find((picture) => picture.hasAttribute('existing'))
    if (existing) existing.selected = true

    // iOS native share (quick add): the URL is pre-filled server-side,
    // trigger scraping immediately without user interaction.
    if (this.autoScrapValue && this.urlTarget.value) {
      this.processed = false
      this.scrap({ target: this.urlTarget })
    }
  }

  #apiScraped(url) {
    return Boolean(this.apiScrapedUrlPatternValue) && new RegExp(this.apiScrapedUrlPatternValue, 'i').test(url)
  }

  disconnect() {
    this.element.removeEventListener('reset', this.#onFormReset)
  }

  #onFormReset = () => {
    this.uploadedPicture = null
    this.#reset()
  }

  scrapUnlessAlreadyScraped(event) {
    if (this.processed) return

    this.scrap(event)
  }

  scrap(event) {
    if (this.processing) return

    // hack to manage 'paste' event
    setTimeout(() => {
      const scraper = new Scraper(event.target.value, { baseUrl: this.baseUrlValue })
      if (!scraper.isScrapable) {
        if (scraper.isInvalid) {
          this.urlErrorTarget.innerText = this.invalidErrorValue
        } else if (scraper.isEmpty) {
          this.urlErrorTarget.innerText = null
        }
        return
      }

      this.#reset()
      this.processing = true
      this.loaderTarget.classList.remove(this.hiddenClass)
      this.clearButtonTarget.classList.add(this.hiddenClass)
      this.refreshTarget.toggleAttribute('data-visible', false)
      this.urlTarget.blur()

      // In the native apps, the phone renders the page and the server parses
      // it; elsewhere, or on failure (null), the server fetches it. Never for a
      // URL the server scrapes through an API (Amazon): the page is not read.
      const rendering = this.#apiScraped(scraper.url)
        ? Promise.resolve(null)
        : phone.renderPage(scraper.url, { refresh: this.refreshPage, currencies: this.currenciesValue })
      this.refreshPage = false

      rendering
        .then((html) => scraper.scrap({ html }))
        .then((product) => {
          this.#manageProduct(product)
        })
        .catch((error) => {
          this.#manageError(error)
          this.#finally()
          if (error.isRefusal) return

          sendError(error, this.identifier, {
            method: 'scrap',
            url: scraper.uri.toString(),
            status: String(error.data?.status ?? ''),
          })
        })
    })
  }

  // In the native apps, the chosen image is downloaded by the phone and sent as
  // a file, like an uploaded photo — the server downloading the URL can be
  // blocked by the merchant, like the page itself. Elsewhere, or on failure,
  // the URL is submitted as before.
  async submit(event) {
    const scrapedCover = !this.coverTarget.disabled && this.coverTarget.value
    if (!phone.canDownloadImage() || !scrapedCover || this.coverDownloaded) return

    event.preventDefault()
    if (this.coverDownloading) return
    this.coverDownloading = true

    const cover = this.coverTarget.value
    const file = await phone.downloadImage(cover, this.urlTarget.value)
    // The picked thumbnail may have changed while the phone was downloading.
    if (file && this.coverTarget.value === cover) {
      const transfer = new DataTransfer()
      transfer.items.add(file)
      // Same channels as a picked photo: the file goes, the URL stays home.
      this.coverFileTarget.disabled = false
      this.coverFileTarget.files = transfer.files
      this.coverTarget.disabled = true
    }

    this.coverDownloaded = true
    this.coverDownloading = false
    // Next task: a quick reply resumes this handler while the first submit
    // event is still dispatching, when the browser ignores requestSubmit.
    setTimeout(() => this.element.requestSubmit(event.submitter))
  }

  selectPicture(event) {
    this.#selectPicture(event.currentTarget)
  }

  toggleClearButton() {
    this.clearButtonTarget.classList.toggle(this.hiddenClass, !this.urlTarget.value)
    if (this.processed) {
      this.processed = false
      this.refreshTarget.toggleAttribute('data-visible', false)
    }
  }

  // The clipboard suggestion of Android keyboards types the text in one go:
  // an `input` event, no `paste`. A real paste is left to its own event. The
  // link may come with words around it ("Regarde ce produit https://…").
  scrapTypedUrl(event) {
    if (event.inputType === 'insertFromPaste' || !/https?:\/\/\S+\.\S+/i.test(event.data ?? '')) return

    this.scrap(event)
  }

  // Re-scrape: same order as any scrape — the phone first, the server if it
  // fails. `refresh` makes the phone load the page again instead of serving
  // the one it already rendered, otherwise the button would change nothing.
  retryScrap() {
    if (this.processing) return

    this.refreshPage = true
    this.scrap({ target: this.urlTarget })
  }

  clearUrl() {
    this.urlTarget.value = ''
    this.clearButtonTarget.classList.add(this.hiddenClass)
    this.refreshTarget.toggleAttribute('data-visible', false)
    // Retirer l'URL = repartir de zéro : les champs remplis par le scrape partent avec.
    this.nameTarget.value = ''
    this.amountTarget.value = ''
    this.#resetCurrency()
    this.#reset()
  }

  // Le slot de la grille ouvre directement le sélecteur de fichier ; la photo
  // choisie devient une vignette de la grille (coverFileChosen) — pas de bascule
  // d'affichage vers la dropzone.
  uploadFromSlot() {
    // Le champ fichier est désactivé tant qu'une vignette scrapée est choisie —
    // un input disabled ignore click() : réarmé ici, coverFileChosen (ou la
    // prochaine sélection d'une vignette) remettra les bons états.
    this.coverFileTarget.disabled = false
    this.coverFileTarget.click()
  }

  // Fichier choisi depuis le slot : la photo rejoint la grille juste après lui,
  // sélectionnée. Re-choisir un fichier remplace cette vignette.
  coverFileChosen() {
    const file = this.coverFileTarget.files[0]
    if (!file) return

    if (!this.uploadedPicture || !this.picturesTarget.contains(this.uploadedPicture)) {
      this.uploadedPicture = this.pictureTemplateTarget.content.cloneNode(true).firstChild
      this.uploadedPicture.uploaded = true
      // L'attribut affiche la croix de retrait (CSS du template).
      this.uploadedPicture.setAttribute('uploaded', '')
      this.uploadSlotTarget.insertAdjacentElement('afterend', this.uploadedPicture)
    }
    // Par l'API de l'élément : il tient déjà sa référence à l'image, inutile
    // de la rechercher après insertion.
    this.uploadedPicture.url = URL.createObjectURL(file)
    this.#selectPicture(this.uploadedPicture)
  }

  // Croix de la photo uploadée : la vignette et le fichier s'en vont, la
  // sélection retombe sur la cover existante, sinon sur la première vignette
  // scrapée, sinon sur rien (canaux neutres, comme après un #reset).
  removeUploadedPicture() {
    const picture = this.uploadedPicture
    if (!picture) return
    const wasSelected = picture.selected
    if (picture.url?.startsWith('blob:')) URL.revokeObjectURL(picture.url)
    picture.remove()
    this.uploadedPicture = null
    this.coverFileTarget.value = ''
    if (!wasSelected) return

    const next = this.pictureTargets.find((item) => item.hasAttribute('existing')) || this.pictureTargets[0]
    if (next) {
      this.#selectPicture(next)
    } else {
      this.coverTarget.value = null
      this.coverTarget.disabled = true
      this.coverFileTarget.disabled = false
    }
  }


  // La case remove_cover cachée porte la suppression au submit ; choisir une
  // autre image la décoche (#selectPicture) — pas de conflit avec la nouvelle cover.
  removeExistingCover() {
    const existing = this.pictureTargets.find((picture) => picture.hasAttribute('existing'))
    if (!existing) return

    const wasSelected = existing.selected
    existing.remove()
    this.removeCoverTarget.checked = true
    if (wasSelected) {
      this.coverTarget.value = null
      this.coverTarget.disabled = true
      this.coverFileTarget.disabled = true
    }
  }

  // Private

  #selectPicture(picture) {
    if (this.hasRemoveCoverTarget) this.removeCoverTarget.checked = false
    const currentPicture = this.pictureTargets.find((item) => item.selected)
    if (currentPicture) currentPicture.selected = false
    picture.selected = true
    // Le serveur privilégie le fichier : un seul des deux canaux (URL scrapée /
    // fichier uploadé) reste actif selon la vignette choisie.
    if (picture.hasAttribute('existing')) {
      // Garder la cover actuelle : aucun canal n'envoie rien.
      this.coverTarget.value = null
      this.coverTarget.disabled = true
      this.coverFileTarget.disabled = true
    } else if (picture.uploaded) {
      this.coverTarget.value = null
      this.coverTarget.disabled = true
      this.coverFileTarget.disabled = false
    } else {
      this.coverTarget.value = picture.url
      this.coverTarget.disabled = false
      this.coverFileTarget.disabled = true
    }
  }

  // Only resets the scrape's presentation state: entered fields (name, price,
  // currency) stay in place — a successful scrape overwrites them via
  // #manageProduct, a failed scrape must not clear them (edit form).
  #reset() {
    this.coverTarget.value = null
    this.#setScrapeResult('')

    this.urlErrorTarget.innerText = null
    this.errorMessageTarget.innerText = null

    this.productFound = false
    this.errorTarget.classList.add(this.hiddenClass)

    // La grille ne garde que le slot d'upload et la cover existante (édition) ;
    // la sélection retombe sur cette dernière, sinon les canaux repartent neutres.
    this.pictureTargets
      .filter((picture) => !picture.hasAttribute('existing'))
      .forEach((picture) => picture.remove())
    const existing = this.pictureTargets.find((picture) => picture.hasAttribute('existing'))
    if (existing) {
      this.#selectPicture(existing)
    } else {
      this.coverFileTarget.disabled = false
      this.coverTarget.disabled = true
    }
  }

  #finally() {
    this.loaderTarget.classList.add(this.hiddenClass)
    if (this.productFound) {
      this.#triggerHaptic('success')
      this.refreshTarget.toggleAttribute('data-visible', true)
    }
    if (this.urlTarget.value) this.clearButtonTarget.classList.remove(this.hiddenClass)
    this.processed = true
    this.processing = false
  }

  #manageError(error) {
    switch (error.message) {
      case 'unprocessable_content':
        if (error.data?.url) this.urlTarget.value = error.data.url
        this.#showDefaultError()
        this.#setScrapeResult('failed')
        break
      case 'error':
        this.#showDefaultError()
        this.#setScrapeResult('failed')
        break
      default:
        console.error(error)
    }
  }

  #showDefaultError() {
    this.errorMessageTarget.innerText = this.defaultErrorValue
    this.errorTarget.classList.remove(this.hiddenClass)
    this.#triggerHaptic('error')
    scrollTo(this.errorTarget, { offset: 100 })
  }

  // Devise rendue par le serveur : celle du produit, sinon celle du profil.
  #resetCurrency() {
    const initial = Array.from(this.currencyTarget.options).find((option) => option.defaultSelected)
    this.currencyTarget.value = initial ? initial.value : 'EUR'
  }

  #manageProduct(product) {
    if (product.url) this.urlTarget.value = product.url
    this.nameTarget.value = product.name
    this.amountTarget.value = product.amount
    // La devise est désormais un select : une devise scrapée hors liste y entre.
    if (product.currency && !this.currencyTarget.querySelector(`option[value="${product.currency}"]`)) {
      this.currencyTarget.add(new Option(product.currency, product.currency))
    }
    if (product.currency) {
      this.currencyTarget.value = product.currency
    } else {
      this.#resetCurrency()
    }

    this.productFound = true
    this.#manageImages(product.pictures)
  }

  #manageImages(pictures) {
    if (pictures.length === 0) {
      this.#finally()
      return
    }

    imagesLoaded(this.#buildImages(pictures)).on('always', (proxy) => {
      const images = proxy.images.filter((image) => this.#imageIsValid(image))
      if (images.length === 0) {
        this.#finally()
        return
      }

      // Re-scrape en édition : les nouvelles images remplacent tout, l'ancienne
      // cover sort de la grille (conservée seulement si le scrape n'a rien donné).
      const existing = this.pictureTargets.find((picture) => picture.hasAttribute('existing'))
      if (existing) existing.remove()

      for (const index in images) {
        const picture = this.#appendPicture(images[index])
        if (index == 0) this.#selectPicture(picture)
      }
      if (this.nameTarget.value && this.amountTarget.value) this.#setScrapeResult('complete')

      this.#finally()
    })
  }

  #appendPicture(image) {
    const picture = this.pictureTemplateTarget.content.cloneNode(true).firstChild
    this.picturesTarget.appendChild(picture)
    picture.url = image.img.currentSrc

    return picture
  }

  #buildImages(pictures) {
    const parentNode = document.createElement('div')

    return pictures.map((picture) => {
      const img = document.createElement('img')
      parentNode.appendChild(img) // necessary for imagesLoaded
      img.src = picture
      return img
    })
  }

  // Sent with the new product: the server credits a complete scrape and penalizes a failed
  // one when deciding on a store review request (doc/notation-stores.md).
  #setScrapeResult(value) {
    if (this.hasScrapeResultTarget) this.scrapeResultTarget.value = value
  }

  #triggerHaptic(type) {
    this.application.getControllerForElementAndIdentifier(document.body, 'bridge--haptic')?.send('trigger', { type })
  }

  #imageIsValid(image) {
    if (!image.isLoaded) return false

    const width = image.img.naturalWidth
    const height = image.img.naturalHeight
    const extension = image.img.currentSrc.substr(image.img.currentSrc.lastIndexOf('.') + 1)

    return (
      width >= this.imageMinWidthValue &&
      height >= this.imageMinHeightValue &&
      width / height <= this.imageMaxRatioValue &&
      extension !== 'svg'
    )
  }
}
